/**
 * Upload Schema — V2 Vouchers
 *
 * Maps to SchemaV2VoucherPost in bldt-ui-fn/v2Vouchers/schemas.ts
 * POST required: amount, currency, recipientType
 */

import { UploadSchema } from '../types';

export const v2VoucherSchema: UploadSchema = {
	entityType: 'v2Voucher',
	label: 'Vouchers',
	apiEndpoint: '/v2/vouchers',
	apiMethod: 'POST',
	fields: [
		{
			name: 'amount',
			label: 'Amount',
			dataType: 'number',
			required: true,
			constraints: { min: 0 },
			headerAliases: ['Voucher Amount', 'Payment Amount', 'Value'],
		},
		{
			name: 'currency',
			label: 'Currency',
			dataType: 'string',
			required: true,
			constraints: { minLength: 3, maxLength: 3 },
			defaultValue: 'USD',
			headerAliases: ['Currency Code', 'Ccy'],
		},
		{
			name: 'recipientType',
			label: 'Recipient Type',
			dataType: 'enum',
			required: true,
			enumValues: ['BENEFICIARY', 'VENDOR'] as const,
			headerAliases: ['Payee Type', 'Recipient', 'Paid To'],
		},
		{
			name: 'recipientEntityId',
			label: 'Recipient',
			dataType: 'relation',
			required: false,
			relation: {
				entityType: 'entity',
				matchField: 'name',
				idField: 'id',
				contextKey: 'entities',
				alternateMatchFields: ['idExternal'],
			},
			headerAliases: ['Payee', 'Beneficiary', 'Vendor Name', 'Recipient Name'],
		},
		{
			name: 'contractId',
			label: 'Contract',
			dataType: 'relation',
			required: false,
			relation: {
				entityType: 'contract',
				matchField: 'name',
				idField: 'id',
				contextKey: 'contracts',
			},
			headerAliases: ['Contract Name', 'Contract Number', 'Contract #'],
		},
		{
			name: 'activityId',
			label: 'Activity',
			dataType: 'relation',
			required: false,
			relation: {
				entityType: 'activity',
				matchField: 'name',
				idField: 'id',
				contextKey: 'activities',
			},
			headerAliases: ['Activity Name', 'Linked Activity'],
		},
		{
			name: 'conditionId',
			label: 'Condition',
			dataType: 'relation',
			required: false,
			relation: {
				entityType: 'condition',
				matchField: 'name',
				idField: 'id',
				contextKey: 'conditions',
			},
			headerAliases: ['Condition Name', 'Linked Condition'],
		},
		// Funding source — a voucher is paid out of exactly one of two outlets,
		// mirroring the Prepare-for-Payment dialog: a spend-plan earmark
		// (spendPlanId) or a direct/standalone source account (fromAccountId).
		// crossFieldValidation below enforces that one — and only one — is set.
		{
			name: 'spendPlanId',
			label: 'Spend Plan',
			dataType: 'relation',
			required: false,
			relation: {
				entityType: 'spendPlan',
				matchField: 'name',
				idField: 'id',
				contextKey: 'spendPlans',
			},
			headerAliases: ['Spend Plan', 'Earmark', 'Spend Plan Name'],
		},
		{
			name: 'fromAccountId',
			label: 'Source Account',
			dataType: 'relation',
			required: false,
			relation: {
				entityType: 'account',
				matchField: 'name',
				idField: 'id',
				contextKey: 'accounts',
			},
			headerAliases: ['Source Account', 'From Account', 'Account', 'Paying Account'],
		},
		{
			name: 'loanId',
			label: 'Financing Source',
			dataType: 'relation',
			required: false,
			relation: {
				entityType: 'loan',
				matchField: 'name',
				idField: 'id',
				contextKey: 'loans',
				alternateMatchFields: ['code'],
			},
			headerAliases: ['Loan', 'Loan Code', 'Financing', 'Fund Source'],
		},
		{
			name: 'channel',
			label: 'Payment Channel',
			dataType: 'enum',
			required: false,
			enumValues: ['BANK', 'CARD', 'MOBILE_MONEY', 'OTHER', 'STABLECOIN'] as const,
			headerAliases: ['Channel', 'Payment Method', 'Disbursement Channel'],
		},
		{
			name: 'bankName',
			label: 'Bank Name',
			dataType: 'string',
			required: false,
			headerAliases: ['Bank', 'Financial Institution'],
		},
		{
			name: 'bankAccountNum',
			label: 'Bank Account Number',
			dataType: 'string',
			required: false,
			headerAliases: ['Account Number', 'Account #', 'Bank Acct'],
		},
		{
			name: 'bankRoutingNum',
			label: 'Bank Routing Number',
			dataType: 'string',
			required: false,
			headerAliases: ['Routing Number', 'Routing #', 'SWIFT', 'BIC'],
		},
		{
			name: 'mobileNumber',
			label: 'Mobile Number',
			dataType: 'string',
			required: false,
			headerAliases: ['Phone', 'Mobile Phone', 'Phone Number'],
		},
		{
			name: 'mobileProvider',
			label: 'Mobile Provider',
			dataType: 'string',
			required: false,
			headerAliases: ['Provider', 'Carrier', 'Mobile Network'],
		},
	],
	// A voucher must be funded from exactly one outlet: a spend-plan earmark
	// or a direct source account — never both, never neither. This mirrors the
	// Prepare-for-Payment dialog's earmark-vs-direct funding toggle.
	crossFieldValidation: (row) => {
		const hasSpendPlan = row.spendPlanId != null && row.spendPlanId !== '';
		const hasAccount = row.fromAccountId != null && row.fromAccountId !== '';
		if (hasSpendPlan && hasAccount) {
			return 'Provide either a Spend Plan or a Source Account, not both.';
		}
		if (!hasSpendPlan && !hasAccount) {
			return 'A funding source is required: provide a Spend Plan or a Source Account.';
		}
		return null;
	},
};
